import Bowser from "bowser";

/**
 * Microphone-troubleshoot guidance resolved for the user's ACTUAL browser + OS.
 *
 * Our users aren't only on the platform default (Chrome on Android / Safari on
 * iOS) — they may be on Firefox, Opera, Samsung Internet, Edge, Brave, etc., and
 * *where the microphone permission lives differs per browser*. So this layer
 * identifies the browser with Bowser (the same UA parser the app uses in
 * `isMobile`) and returns steps tailored to it, always with a device-Settings
 * `fallback` that works for ANY browser on that OS — so an unrecognised browser
 * still gets an actionable path.
 *
 * Notes on the mobile permission model:
 *  - iOS: every browser is a WebKit wrapper. Only Safari exposes a per-site
 *    control ("aA" → Website Settings); all others grant the mic at the app
 *    level, so the step is "iOS Settings → <that browser> → Microphone".
 *  - Android: each browser has its own site-permission menu (reached from the
 *    lock / site-info icon) plus an app-level permission in system Settings.
 *
 * @returns {{ browserName: string, osName: string, steps: string[], fallback: string|null }}
 */
export function getMicTroubleshoot() {
  const parser = Bowser.getParser(window.navigator.userAgent || "");
  const os = (parser.getOSName(true) || "").toLowerCase(); // 'ios' | 'android' | …
  const browserKey = (parser.getBrowserName(true) || "").toLowerCase();
  const browserName = parser.getBrowserName() || "your browser";

  const guide =
    os === "ios"
      ? iosGuide(browserKey, browserName)
      : os === "android"
      ? androidGuide(browserKey, browserName)
      : genericGuide(browserName);

  return { browserName, osName: parser.getOSName() || "", ...guide };
}

// ---- iOS -------------------------------------------------------------------

function iosGuide(browserKey, name) {
  if (browserKey === "safari") {
    return {
      steps: [
        'Tap the "aA" icon on the left of the address bar.',
        "Tap Website Settings.",
        "Set Microphone to Allow.",
        "Tap Done, then reload the page.",
      ],
      fallback:
        "Still blocked? Open Settings → Safari → Microphone and allow it.",
    };
  }

  // Chrome / Firefox / Edge / Opera / Brave / … on iOS are all WebKit wrappers
  // and grant the mic at the app level — there is no per-site control.
  return {
    steps: [
      "Open the iOS Settings app.",
      `Scroll down and tap ${name}.`,
      "Turn Microphone on.",
      "Return here and reload the page.",
    ],
    fallback: null,
  };
}

// ---- Android ---------------------------------------------------------------

function androidGuide(browserKey, name) {
  const appFallback = `Still blocked? Open Settings → Apps → ${name} → Permissions → Microphone → Allow.`;

  switch (browserKey) {
    // Chromium family — Chrome, Edge, Brave (Bowser reports Brave as Chrome).
    case "chrome":
    case "microsoft edge":
    case "brave":
      return {
        steps: [
          "Tap the lock (or tune) icon to the left of the address bar.",
          "Tap Permissions (or Site settings).",
          "Set Microphone to Allow, then reload the page.",
        ],
        fallback: appFallback,
      };

    case "samsung internet":
      return {
        steps: [
          "Tap the page/lock icon to the left of the address bar.",
          "Tap Permissions.",
          "Set Microphone to Allow, then reload the page.",
        ],
        fallback: appFallback,
      };

    case "firefox":
      return {
        steps: [
          "Tap the lock/shield icon to the left of the address bar.",
          "Tap Clear permissions — or open the menu (⋮) → Settings → Site permissions → Microphone.",
          "Reload the page and tap Allow when asked.",
        ],
        fallback: appFallback,
      };

    case "opera":
      return {
        steps: [
          "Tap the lock icon to the left of the address bar.",
          "Tap Site settings.",
          "Set Microphone to Allow, then reload the page.",
        ],
        fallback: appFallback,
      };

    // Any other Android browser (UC, MIUI, DuckDuckGo, Vivaldi, …).
    default:
      return {
        steps: [
          "Tap the lock (or site-info) icon next to the address bar.",
          "Open Permissions / Site settings.",
          "Set Microphone to Allow, then reload the page.",
        ],
        fallback: appFallback,
      };
  }
}

// ---- Fallback (non-mobile OS / unknown) ------------------------------------

function genericGuide(name) {
  return {
    steps: [
      "Open your browser's site settings for this page.",
      "Find the Microphone permission.",
      "Set it to Allow, then reload the page.",
    ],
    fallback: `Still blocked? Allow microphone for ${name} in your device settings.`,
  };
}
